import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const CATS = [
  { k: "astrology", l: "Astrology & Jyotish" },
  { k: "aarti", l: "Aarti & Puja" },
  { k: "puja", l: "Vedic Rituals" },
  { k: "lecture", l: "Scholarly Discourses" },
  { k: "bhajan", l: "Bhajan Singing" },
  { k: "music", l: "Devotional Music" },
  { k: "wedding", l: "Wedding Ceremonies" },
  { k: "household", l: "Household Pujas" },
  { k: "event", l: "Event Services (griha pravesh, naamkaran, etc.)" },
  { k: "vastu", l: "Vastu Shastra" },
  { k: "tarot", l: "Tarot Reading" },
];

const LANGS = ["English", "Hindi", "Sanskrit", "Tamil", "Telugu", "Marathi", "Bengali", "Kannada", "Malayalam"];

export default function TeacherApply() {
  const [form, setForm] = useState({
    name: "", email: "", password: "", title: "", bio: "",
    categories: [], languages: ["English", "Hindi"], location: "", photo_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) return toast.error("Pick at least one category");
    setSubmitting(true);
    try {
      const { data } = await api.post("/gurus/apply", form);
      localStorage.setItem("pd_token", data.access_token);
      setDone(true);
      toast.success("Application submitted — admin review within 24 hours");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="max-w-xl mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#2F7D32] mx-auto" />
        <h1 className="font-serif text-4xl text-[#4A0E17] mt-5">॥ स्वागतम् गुरुजी ॥</h1>
        <p className="text-[#63534E] mt-4">
          Your application is received. Our admin team will review and approve within 24 hours. You can preview your dashboard now; services become listable once approved.
        </p>
        <Link to="/teacher/dashboard" data-testid="go-dashboard" className="btn-primary mt-8">Open my dashboard</Link>
      </div>
    );
  }

  return (
    <div data-testid="teacher-apply-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">Apply to Teach</div>
      <h1 className="font-serif text-5xl text-[#4A0E17] mt-2 leading-tight">Share your service with the sangha</h1>
      <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
        Gurus, astrologers, scholars, Vastu consultants and tarot readers — list your services and earn <strong className="text-[#E65100]">80% of every booking</strong>. The platform retains 20% to support Pancham Dham operations.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5 bg-[#FFFDF9] sacred-border rounded-3xl p-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="apply-name" required placeholder="Full name (incl. title)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="apply-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="apply-password" required type="password" placeholder="Password (6+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="apply-location" placeholder="Location (e.g. Varanasi)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <Input data-testid="apply-title" required placeholder="Professional title (e.g., Vedic Astrologer · 25 yrs)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        <Input data-testid="apply-photo" placeholder="Photo URL (optional, use any public image URL)" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        <Textarea data-testid="apply-bio" required placeholder="Your bio — tradition, teachers, 20+ words." rows={5} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-white border-[#D4AF37]/40" />
        <div>
          <label className="overline">Categories (pick all that apply)</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button type="button" key={c.k} data-testid={`cat-${c.k}`} onClick={() => setForm({ ...form, categories: toggle(form.categories, c.k) })}
                className={`px-3 h-10 rounded-full text-sm transition-sacred border ${form.categories.includes(c.k) ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                {c.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="overline">Languages you teach in</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {LANGS.map((l) => (
              <button type="button" key={l} data-testid={`lang-${l.toLowerCase()}`} onClick={() => setForm({ ...form, languages: toggle(form.languages, l) })}
                className={`px-3 h-9 rounded-full text-xs transition-sacred border ${form.languages.includes(l) ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <Button data-testid="apply-submit" type="submit" disabled={submitting} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-12 text-base">
          {submitting ? "Submitting…" : "Submit application"}
        </Button>
        <p className="text-xs text-[#63534E] text-center">By applying you agree to the 80/20 revenue split and platform guidelines.</p>
      </form>
    </div>
  );
}
